/* Horizon >=990px: `.page-wrapper` is the scroll container, so the scroll listener rebinds at that breakpoint. */
(() => {
  const DESKTOP = window.matchMedia('(min-width: 750px)');
  const SQUEEZE = window.matchMedia('(min-width: 990px)');
  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

  const pageWrapper = () => (SQUEEZE.matches ? document.querySelector('.page-wrapper') : null);
  const scrollTargetOf = () => pageWrapper() ?? document;
  const scrollerOf = () => pageWrapper() ?? document.scrollingElement ?? document.documentElement;

  const clamp = (n, min, max) => Math.min(Math.max(n, min), max);

  const init = (root) => {
    if (root.bronteStory) return;

    const scroll = root.querySelector('[data-story-scroll]');
    const stage = root.querySelector('[data-story-stage]');
    const chapters = [...root.querySelectorAll('[data-story-chapter]')];
    // One chapter has nothing to pin for, so it reads better in flow.
    if (!scroll || !stage || chapters.length < 2) return;

    let index = -1;
    let rafId = 0;
    let bound = null;
    let running = false;

    const apply = (next) => {
      const i = clamp(next, 0, chapters.length - 1);
      if (i === index) return;
      index = i;
      chapters.forEach((el, n) => {
        el.dataset.state = n < i ? 'past' : n === i ? 'active' : 'pending';
      });
    };

    const measure = () => {
      const rect = scroll.getBoundingClientRect();
      const travel = rect.height - stage.offsetHeight;
      if (travel <= 0) {
        apply(0);
        return;
      }
      const progress = clamp(-rect.top / travel, 0, 1);
      apply(Math.floor(progress * chapters.length));
    };

    const onScroll = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        rafId = 0;
        measure();
      });
    };

    const unbind = () => {
      if (bound) bound.removeEventListener('scroll', onScroll);
      bound = null;
    };

    const bind = () => {
      const target = scrollTargetOf();
      if (bound === target) return;
      unbind();
      bound = target;
      target.addEventListener('scroll', onScroll, { passive: true });
    };

    const start = () => {
      running = true;
      bind();
      measure();
    };

    const stop = () => {
      running = false;
      unbind();
      if (rafId) {
        cancelAnimationFrame(rafId);
        rafId = 0;
      }
    };

    const gate =
      'IntersectionObserver' in window
        ? new IntersectionObserver(
            (entries) => {
              if (entries.some((entry) => entry.isIntersecting)) start();
              else stop();
            },
            { rootMargin: '200px 0px' }
          )
        : null;

    const enable = () => {
      if (DESKTOP.matches && !REDUCED.matches) {
        root.dataset.storyReady = 'true';
        index = -1;
        if (gate) gate.observe(root);
        else start();
      } else {
        delete root.dataset.storyReady;
        if (gate) gate.unobserve(root);
        stop();
      }
    };

    /* Theme editor: scroll the stage to the selected block's dwell so the block is reviewable. */
    const jumpTo = (chapter) => {
      const i = chapters.indexOf(chapter);
      if (i < 0) return;
      apply(i);
      if (!running) return;

      const rect = scroll.getBoundingClientRect();
      const travel = rect.height - stage.offsetHeight;
      if (travel <= 0) return;

      const step = travel / chapters.length;
      const scroller = scrollerOf();
      scroller.scrollTo({ top: scroller.scrollTop + rect.top + (i + 0.5) * step, behavior: 'instant' });
    };

    const onResize = () => {
      if (running) measure();
    };
    const onSqueeze = () => {
      if (running) {
        bind();
        measure();
      }
    };

    DESKTOP.addEventListener('change', enable);
    REDUCED.addEventListener('change', enable);
    SQUEEZE.addEventListener('change', onSqueeze);
    window.addEventListener('resize', onResize, { passive: true });

    root.bronteStory = {
      jumpTo,
      destroy() {
        stop();
        if (gate) gate.disconnect();
        DESKTOP.removeEventListener('change', enable);
        REDUCED.removeEventListener('change', enable);
        SQUEEZE.removeEventListener('change', onSqueeze);
        window.removeEventListener('resize', onResize);
        delete root.bronteStory;
      },
    };

    enable();
  };

  const boot = (scope) => (scope ?? document).querySelectorAll('[data-bronte-about-story]').forEach(init);

  if (document.readyState !== 'loading') boot();
  else document.addEventListener('DOMContentLoaded', () => boot());

  document.addEventListener('shopify:section:load', (event) => boot(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-bronte-about-story]').forEach((root) => root.bronteStory?.destroy());
  });

  document.addEventListener('shopify:block:select', (event) => {
    const chapter = event.target.closest?.('[data-story-chapter]');
    chapter?.closest('[data-bronte-about-story]')?.bronteStory?.jumpTo(chapter);
  });
})();
